/**
 * Public-share DTO mappers
 *
 * Pure DTO → app-model mappers for the public share/embed pages. They let the
 * authenticated map pipeline (UnifiedAnchorSymbolLayer, TaskGeometryOverlays,
 * hover previews, detail tabs) render public data unchanged. Fields the public
 * API never exposes (author ids, history, related tasks, contractor, internal
 * notes) are filled with safe empties.
 *
 * Two families: the `*ForMap` / `*ListItemToNote` mappers consume the slim MAP
 * and LIST DTOs (markers, pins, list rows); the `mapPublic*Detail` /
 * `mapPublicTask` mappers consume the full DETAIL DTOs fetched on artifact open.
 *
 * @module store/mappers/publicShareMapper
 */
import { mapGeometryFromApi } from '@/store/mappers/geometryMapper';
import { mapObstructionGeoData } from '@/store/mappers/obstructionMapper';
import type {
  Attachment,
  Coordinates,
  RelatedTaskSummary,
  Requirement,
  Task,
  TaskComment,
  TaskGeometry,
  TaskStatus,
  Priority,
} from '@/types';
import { NoteVisibility } from '@/types/notes/NoteVisibility';
import type { Note } from '@/types/notes/Note';
import type { NoteAttachment } from '@/types/notes/NoteAttachment';
import type { NoteMapItem } from '@/types/notes/NoteMapItem';
import type {
  Obstruction,
  ObstructionContractor,
  ObstructionMapItem,
} from '@/types/obstructions';
import type {
  ObstructionGeometryType,
  ObstructionStatus,
  ObstructionType,
  SeverityLevel,
  TrafficImpact,
} from '@/types/obstructions';
import type {
  PublicGeometry,
  PublicNote,
  PublicNoteListItem,
  PublicNoteMapItem,
  PublicObstruction,
  PublicObstructionMapItem,
  PublicTask,
  PublicTaskMapItem,
  PublicTaskSummary,
} from '@/types/share';

/** Maps a public task summary to the app `RelatedTaskSummary` shape. */
function mapPublicTaskSummary(summary: PublicTaskSummary): RelatedTaskSummary {
  return {
    id: summary.id,
    shortId: summary.shortId,
    title: summary.title,
    status: summary.status as TaskStatus,
    location: summary.location,
    dueDate: summary.dueDate ? new Date(summary.dueDate) : null,
  };
}

/**
 * Convert a public geometry DTO to a `TaskGeometry`. The public `geoData` blob
 * is identical to the authenticated geometry response, so we delegate to the
 * canonical {@link mapGeometryFromApi} mapper via a thin adapter. The artifact
 * anchor is used as the calculated-center fallback when the geoData carries no
 * explicit coordinates.
 */
function mapPublicGeometry(
  geometry: PublicGeometry,
  anchor: Coordinates
): TaskGeometry {
  const mapped = mapGeometryFromApi({
    id: geometry.id,
    type: geometry.type,
    geoData: geometry.geoData,
    calculatedCenter: { lat: anchor.lat, lng: anchor.lng },
  });
  // The adapter object lacks `taskId`, so `mapGeometryFromApi` treats it as the
  // minimal map DTO and drops name/description. Restore them from the public DTO,
  // which carries both fields.
  return {
    ...mapped,
    name: geometry.name,
    description: geometry.description ?? undefined,
  };
}

/**
 * Maps a public task DTO to the full app `Task` shape. Detail collections
 * (requirements / comments / attachments) are attached directly so the detail
 * panel renders without any authenticated fetch; fields the public API never
 * exposes (author ids, history, related tasks) stay empty.
 */
export function mapPublicTask(publicTask: PublicTask): Task {
  const anchor = { lat: publicTask.anchorLat, lng: publicTask.anchorLng };

  const requirements: Requirement[] = (publicTask.requirements ?? []).map(
    (requirement, index) => ({
      id: `${publicTask.id}-requirement-${index}`,
      text: requirement.text,
      completed: requirement.isCompleted,
      order: index,
      createdAt: new Date(publicTask.createdAt),
    })
  );

  const attachments: Attachment[] = (publicTask.attachments ?? []).map(
    (attachment) => ({
      id: attachment.id,
      name: attachment.fileName,
      type: attachment.contentType,
      size: attachment.fileSize,
      data: '',
      uploadedAt: new Date(attachment.uploadedAt),
    })
  );

  const comments: TaskComment[] = (publicTask.comments ?? []).map(
    (comment) => ({
      id: comment.id,
      message: comment.message,
      // Empty author = identity hidden by the share's personal-data flag;
      // the comments UI renders an anonymous fallback for empty names.
      author: comment.authorName ?? '',
      authorId: '',
      authorAvatarUrl: comment.authorAvatarUrl,
      createdAt: new Date(comment.createdAt),
    })
  );

  return {
    id: publicTask.id,
    shortId: publicTask.shortId,
    title: publicTask.title,
    description: publicTask.description,
    status: publicTask.status as TaskStatus,
    priority: publicTask.priority as Priority,
    assignedToId: null,
    assignedToName: publicTask.assigneeName,
    assignedToAvatarUrl: null,
    assignedToIsFormerMember: false,
    startDate: publicTask.startDate ? new Date(publicTask.startDate) : null,
    dueDate: publicTask.dueDate ? new Date(publicTask.dueDate) : null,
    completedAt: null,
    location: publicTask.location ?? undefined,
    anchorPoint: anchor,
    geometries: publicTask.geometries.map((geometry) =>
      mapPublicGeometry(geometry, anchor)
    ),
    requirements,
    attachments,
    comments,
    history: [],
    relatedTasks: publicTask.relatedTasks.map(mapPublicTaskSummary),
    requireRelatedTasksCompletion: false,
    requirementCount: publicTask.requirements?.length,
    completedRequirementCount: publicTask.requirements?.filter(
      (requirement) => requirement.isCompleted
    ).length,
    attachmentCount: publicTask.attachments?.length,
    commentCount: publicTask.comments?.length,
    createdAt: new Date(publicTask.createdAt),
    updatedAt: publicTask.updatedAt ? new Date(publicTask.updatedAt) : null,
  };
}

/**
 * Maps a slim public MAP task to the app `Task` shape consumed by the map
 * pipeline (anchor symbol layer, geometry overlays, hover tooltip). Only the
 * fields the map + tooltip read are populated; detail collections stay empty
 * and are hydrated separately when the task is opened (see {@link mapPublicTask}).
 */
export function mapPublicTaskForMap(item: PublicTaskMapItem): Task {
  const anchor = { lat: item.anchorLat, lng: item.anchorLng };

  return {
    id: item.id,
    shortId: item.shortId,
    title: item.title,
    description: '',
    status: item.status as TaskStatus,
    priority: item.priority as Priority,
    assignedToId: null,
    assignedToName: item.assigneeName,
    assignedToAvatarUrl: item.assigneeAvatarUrl,
    assignedToIsFormerMember: false,
    startDate: null,
    dueDate: item.dueDate ? new Date(item.dueDate) : null,
    completedAt: null,
    location: undefined,
    anchorPoint: anchor,
    geometries: item.geometries.map((geometry) =>
      mapPublicGeometry(geometry, anchor)
    ),
    requirements: [],
    attachments: [],
    comments: [],
    history: [],
    relatedTasks: [],
    requireRelatedTasksCompletion: false,
    requirementCount: item.requirementCount,
    completedRequirementCount: item.completedRequirementCount,
    attachmentCount: undefined,
    commentCount: undefined,
    // The map tooltip never reads `createdAt`; the slim MAP item omits it, so a
    // fixed epoch sentinel keeps the `Task` shape valid without a fabricated now.
    createdAt: new Date(0),
    updatedAt: null,
  };
}

/** Maps a slim public MAP note to the `NoteMapItem` consumed by the note pin layer. */
export function mapPublicNoteForMap(item: PublicNoteMapItem): NoteMapItem {
  return {
    id: item.id,
    shortId: item.shortId,
    title: item.title,
    latitude: item.anchorLat,
    longitude: item.anchorLng,
    location: item.location,
    color: item.color,
    visibility: NoteVisibility.Workspace,
    authorId: '',
    creatorName: item.creatorName ?? '',
    creatorAvatarUrl: item.creatorAvatarUrl,
    linkedTasks: item.linkedTasks.map((linkedTask) => ({
      id: linkedTask.id,
      shortId: linkedTask.shortId,
      title: linkedTask.title,
    })),
    createdAt: item.createdAt,
    updatedAt: item.updatedAt,
  };
}

/**
 * Maps a slim public note LIST item to the minimal app `Note` needed by
 * `NoteCard` (color dot + title + visibility badge + creator + date). Content,
 * attachments, and links are empty — list rows never read them.
 */
export function mapPublicNoteListItemToNote(item: PublicNoteListItem): Note {
  return {
    id: item.id,
    shortId: item.shortId,
    title: item.title,
    content: '{}',
    latitude: 0,
    longitude: 0,
    location: null,
    color: item.color,
    visibility: NoteVisibility.Workspace,
    createdBy: '',
    creatorName: item.creatorName ?? '',
    creatorAvatarUrl: item.creatorAvatarUrl,
    attachmentCount: 0,
    linkedTaskCount: 0,
    attachments: [],
    linkedTaskIds: [],
    linkedTasks: [],
    createdAt: new Date(item.createdAt),
    updatedAt: item.updatedAt ? new Date(item.updatedAt) : null,
  };
}

/**
 * Maps a public note DTO to the full app `Note` shape consumed by the
 * read-only detail panel (metadata section, minimap, attachments, linked
 * task). Fields the public API never exposes (creator id, visibility beyond
 * workspace) are filled with safe empties.
 */
export function mapPublicNoteDetail(publicNote: PublicNote): Note {
  const attachments: NoteAttachment[] = (publicNote.attachments ?? []).map(
    (attachment) => ({
      id: attachment.id,
      fileName: attachment.fileName,
      contentType: attachment.contentType,
      fileSize: attachment.fileSize,
      createdAt: new Date(attachment.uploadedAt),
    })
  );

  const linkedTasks = publicNote.linkedTasks.map(mapPublicTaskSummary);

  return {
    id: publicNote.id,
    shortId: publicNote.shortId,
    title: publicNote.title,
    content: publicNote.content,
    latitude: publicNote.anchorLat,
    longitude: publicNote.anchorLng,
    location: publicNote.location,
    color: publicNote.color,
    visibility: NoteVisibility.Workspace,
    createdBy: '',
    creatorName: publicNote.creatorName ?? '',
    creatorAvatarUrl: publicNote.creatorAvatarUrl,
    attachmentCount: attachments.length,
    linkedTaskCount: linkedTasks.length,
    attachments,
    linkedTaskIds: linkedTasks.map((linkedTask) => linkedTask.id),
    linkedTasks,
    createdAt: new Date(publicNote.createdAt),
    updatedAt: publicNote.updatedAt ? new Date(publicNote.updatedAt) : null,
  };
}

/** Maps a slim public MAP obstruction to the `ObstructionMapItem` map shape. */
export function mapPublicObstructionForMap(
  item: PublicObstructionMapItem
): ObstructionMapItem {
  const anchor = { lat: item.anchorLat, lng: item.anchorLng };

  return {
    id: item.id,
    shortId: item.shortId,
    title: item.title,
    type: item.type as ObstructionType,
    status: item.status as ObstructionStatus,
    severity: item.severity as SeverityLevel,
    impact: item.impact as TrafficImpact,
    anchorPoint: anchor,
    geometries: item.geometries.map((geometry) => ({
      id: geometry.id,
      type: geometry.type as ObstructionGeometryType,
      calculatedCenter: anchor,
      geoData: mapObstructionGeoData(geometry.geoData),
    })),
  };
}

/** Empty contractor block — the public API never exposes contractor data. */
const EMPTY_PUBLIC_CONTRACTOR: ObstructionContractor = {
  company: '',
  manager: '',
  phone: '',
  email: null,
  emergencyContact: null,
  permitNumber: null,
  permitIssuedDate: null,
  permitExpiryDate: null,
  notes: null,
};

/**
 * Maps a public obstruction DTO to the full app `Obstruction` shape consumed by
 * the read-only detail panel. Fields the public API never exposes (workspace id,
 * internal notes, actual dates, contractor, signs, authorship, full geometries)
 * are filled with safe empties; `createdAt` falls back to `plannedStartDate`.
 */
export function mapPublicObstructionDetail(
  publicObstruction: PublicObstruction
): Obstruction {
  const anchor = {
    lat: publicObstruction.anchorLat,
    lng: publicObstruction.anchorLng,
  };

  return {
    id: publicObstruction.id,
    shortId: publicObstruction.shortId,
    workspaceId: '',
    title: publicObstruction.title,
    description: publicObstruction.description,
    type: publicObstruction.type as ObstructionType,
    status: publicObstruction.status as ObstructionStatus,
    severity: publicObstruction.severity as SeverityLevel,
    impact: publicObstruction.impact as TrafficImpact,
    location: publicObstruction.road,
    internalNotes: null,
    plannedStartDate: new Date(publicObstruction.plannedStartDate),
    plannedEndDate: new Date(publicObstruction.plannedEndDate),
    actualStartDate: null,
    actualEndDate: null,
    contractor: EMPTY_PUBLIC_CONTRACTOR,
    anchorPoint: anchor,
    geometries: [],
    signs: [],
    affectedRoads: [],
    attachments: [],
    createdBy: '',
    creatorName: '',
    creatorAvatarUrl: null,
    creatorIsFormerMember: false,
    createdAt: new Date(publicObstruction.plannedStartDate),
    updatedAt: null,
  };
}
